/** Seed data for the Procurement Overview dashboard (Demo Mode only). A cross-cutting
 * "control tower" landing page for the Procurement module - surfaces supplier, catalogue,
 * pricing and site-readiness exceptions across the client account / site group / site
 * hierarchy so a Procurement user can see where attention is needed without hopping between
 * the Suppliers, Catalogues and Products list screens. Entirely mock-data driven, no backend
 * feed exists yet - mirrors the same seeded/illustrative approach used by
 * src/pages/ProcurementRollup and src/pages/EstateManagement/EstateOverview. */

export type ExceptionSeverity = 'Info' | 'Warning' | 'Critical';

export const CLIENT_ACCOUNTS = ['St Mary\u2019s Trust', 'Northgate NHS Trust', 'Ridgeway Council'] as const;

export const SITE_GROUPS: Record<string, string[]> = {
  'St Mary\u2019s Trust': ['Education - North', 'Education - South'],
  'Northgate NHS Trust': ['Healthcare - Acute', 'Healthcare - Community'],
  'Ridgeway Council': ['Corporate Dining'],
};

export interface ProcurementSite {
  code: string;
  name: string;
  clientAccount: string;
  siteGroup: string;
}

export const SITES: ProcurementSite[] = [
  { code: '0101', name: 'High Street Central', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North' },
  { code: '0102', name: 'Riverside School', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North' },
  { code: '0145', name: 'Fairview Academy', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - South' },
  { code: '0201', name: 'Northgate General', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Acute' },
  { code: '0202', name: 'Downtown Community Clinic', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Community' },
  { code: '0301', name: 'Uptown Offices', clientAccount: 'Ridgeway Council', siteGroup: 'Corporate Dining' },
];

export const SUPPLIER_CATEGORIES = ['Fresh Produce', 'Dairy', 'Meat & Poultry', 'Dry Goods', 'Packaging'] as const;
export const SUPPLIER_STATUSES = ['Active', 'Pending Approval', 'Suspended', 'Onboarding'] as const;

export interface SupplierOverviewRow {
  id: number;
  supplierName: string;
  category: string;
  status: string;
  clientAccount: string;
  siteGroup: string;
  issue: string;
  severity: ExceptionSeverity;
  missingDocuments: boolean;
  expiredCertification: boolean;
}

export const supplierExceptions: SupplierOverviewRow[] = [
  {
    id: 1, supplierName: 'Greenfield Farms', category: 'Fresh Produce', status: 'Active',
    clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    issue: 'BRCGS Food Safety certificate expired 2026-06-30.', severity: 'Critical',
    missingDocuments: false, expiredCertification: true,
  },
  {
    id: 2, supplierName: 'Coastal Seafoods Ltd', category: 'Meat & Poultry', status: 'Pending Approval',
    clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Acute',
    issue: 'Onboarding review outstanding - no insurance documents on file.', severity: 'Warning',
    missingDocuments: true, expiredCertification: false,
  },
  {
    id: 3, supplierName: 'Northern Dairy Co-op', category: 'Dairy', status: 'Active',
    clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - South',
    issue: 'ISO 22000 certificate expiring within 30 days.', severity: 'Warning',
    missingDocuments: false, expiredCertification: false,
  },
  {
    id: 4, supplierName: 'Prime Cuts Butchery', category: 'Meat & Poultry', status: 'Suspended',
    clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Community',
    issue: 'Supplier suspended pending food safety incident investigation.', severity: 'Critical',
    missingDocuments: false, expiredCertification: false,
  },
  {
    id: 5, supplierName: 'EcoPack Supplies', category: 'Packaging', status: 'Onboarding',
    clientAccount: 'Ridgeway Council', siteGroup: 'Corporate Dining',
    issue: 'Public liability insurance not yet submitted.', severity: 'Warning',
    missingDocuments: true, expiredCertification: false,
  },
  {
    id: 6, supplierName: 'Harvest Grain Merchants', category: 'Dry Goods', status: 'Active',
    clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    issue: 'Sustainability accreditation review overdue.', severity: 'Info',
    missingDocuments: false, expiredCertification: false,
  },
];

export interface CatalogueOverviewRow {
  id: number;
  catalogueName: string;
  itemNumber: string;
  itemName: string;
  status: 'Live' | 'Awaiting Approval' | 'Data Issue';
  clientAccount: string;
  siteGroup: string;
  issue: string;
  severity: ExceptionSeverity;
}

export const catalogueExceptions: CatalogueOverviewRow[] = [
  {
    id: 1, catalogueName: 'Education Core Catalogue', itemNumber: 'BTR-002', itemName: 'Unsalted Butter Block 10kg',
    status: 'Awaiting Approval', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    issue: 'Supplier price increase of 8% submitted - awaiting commercial approval.', severity: 'Warning',
  },
  {
    id: 2, catalogueName: 'Healthcare Catering Catalogue', itemNumber: 'SPC-009', itemName: 'Mixed Spice Blend 1kg',
    status: 'Data Issue', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Acute',
    issue: 'Ingredient recall in effect - missing allergen data.', severity: 'Critical',
  },
  {
    id: 3, catalogueName: 'Corporate Dining Catalogue', itemNumber: 'OLV-002', itemName: 'Extra Virgin Olive Oil 5L',
    status: 'Data Issue', clientAccount: 'Ridgeway Council', siteGroup: 'Corporate Dining',
    issue: 'Missing nutritional information and unit cost mismatch.', severity: 'Warning',
  },
  {
    id: 4, catalogueName: 'Education Core Catalogue', itemNumber: 'CRS-100', itemName: 'Wholemeal Bread Rolls',
    status: 'Live', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - South',
    issue: 'No open issues.', severity: 'Info',
  },
  {
    id: 5, catalogueName: 'Healthcare Catering Catalogue', itemNumber: 'FLR-001', itemName: 'Strong White Flour 16kg',
    status: 'Live', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Community',
    issue: 'No open issues.', severity: 'Info',
  },
];

export interface PendingPriceChangeRow {
  id: number;
  itemNumber: string;
  itemName: string;
  supplierName: string;
  clientAccount: string;
  siteGroup: string;
  currentPrice: number;
  proposedPrice: number;
  submittedDate: string;
  severity: ExceptionSeverity;
}

export const pendingPriceChanges: PendingPriceChangeRow[] = [
  {
    id: 1, itemNumber: 'BTR-002', itemName: 'Unsalted Butter Block 10kg', supplierName: 'Northern Dairy Co-op',
    clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    currentPrice: 38.9, proposedPrice: 42.0, submittedDate: '2026-06-28', severity: 'Warning',
  },
  {
    id: 2, itemNumber: 'FLR-001', itemName: 'Strong White Flour 16kg', supplierName: 'Harvest Grain Merchants',
    clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - South',
    currentPrice: 12.4, proposedPrice: 13.1, submittedDate: '2026-07-01', severity: 'Info',
  },
  {
    id: 3, itemNumber: 'MPC-014', itemName: 'Chicken Breast Fillets 5kg', supplierName: 'Prime Cuts Butchery',
    clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Community',
    currentPrice: 24.5, proposedPrice: 29.9, submittedDate: '2026-06-15', severity: 'Critical',
  },
];

export interface UnavailableProductRow {
  id: number;
  itemNumber: string;
  itemName: string;
  supplierName: string;
  clientAccount: string;
  siteGroup: string;
  reason: string;
  sitesAffected: number;
  severity: ExceptionSeverity;
}

export const unavailableProducts: UnavailableProductRow[] = [
  {
    id: 1, itemNumber: 'SPC-009', itemName: 'Mixed Spice Blend 1kg', supplierName: 'Coastal Seafoods Ltd',
    clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Acute',
    reason: 'Ingredient recall in effect - blocked from ordering.', sitesAffected: 2, severity: 'Critical',
  },
  {
    id: 2, itemNumber: 'MPC-014', itemName: 'Chicken Breast Fillets 5kg', supplierName: 'Prime Cuts Butchery',
    clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Community',
    reason: 'Supplier suspended - no alternate source assigned.', sitesAffected: 1, severity: 'Critical',
  },
  {
    id: 3, itemNumber: 'OLV-002', itemName: 'Extra Virgin Olive Oil 5L', supplierName: 'EcoPack Supplies',
    clientAccount: 'Ridgeway Council', siteGroup: 'Corporate Dining',
    reason: 'Out of stock at supplier - awaiting restock date.', sitesAffected: 1, severity: 'Warning',
  },
];

export interface SiteReadinessRow {
  siteCode: string;
  siteName: string;
  clientAccount: string;
  siteGroup: string;
  suppliersLinked: number;
  cataloguesAssigned: number;
  openExceptions: number;
  readiness: 'Ready' | 'At Risk' | 'Not Ready';
  severity: ExceptionSeverity;
}

export const siteReadiness: SiteReadinessRow[] = [
  {
    siteCode: '0101', siteName: 'High Street Central', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    suppliersLinked: 6, cataloguesAssigned: 1, openExceptions: 1, readiness: 'Ready', severity: 'Info',
  },
  {
    siteCode: '0102', siteName: 'Riverside School', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - North',
    suppliersLinked: 5, cataloguesAssigned: 1, openExceptions: 2, readiness: 'At Risk', severity: 'Warning',
  },
  {
    siteCode: '0145', siteName: 'Fairview Academy', clientAccount: 'St Mary\u2019s Trust', siteGroup: 'Education - South',
    suppliersLinked: 4, cataloguesAssigned: 1, openExceptions: 0, readiness: 'Ready', severity: 'Info',
  },
  {
    siteCode: '0201', siteName: 'Northgate General', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Acute',
    suppliersLinked: 0, cataloguesAssigned: 0, openExceptions: 1, readiness: 'Not Ready', severity: 'Critical',
  },
  {
    siteCode: '0202', siteName: 'Downtown Community Clinic', clientAccount: 'Northgate NHS Trust', siteGroup: 'Healthcare - Community',
    suppliersLinked: 3, cataloguesAssigned: 1, openExceptions: 2, readiness: 'At Risk', severity: 'Warning',
  },
  {
    siteCode: '0301', siteName: 'Uptown Offices', clientAccount: 'Ridgeway Council', siteGroup: 'Corporate Dining',
    suppliersLinked: 4, cataloguesAssigned: 1, openExceptions: 1, readiness: 'Ready', severity: 'Info',
  },
];
